<template>
  <div class="ly-analysis">
    <!-- Панели плоские: перспектива таблицы только портит, поэтому уносим их из .ly-cam
         в .ly-stage. Пока сцены нет — рисуем на месте. Комментарий внутри корня: иначе
         корней два и $el — не div. -->
    <Teleport :to="stageEl" :disabled="!stageEl">
      <!-- панель чтения: текст передней карточки, пока листаете стопку -->
      <div v-if="read" class="ly-spanel" @mousedown.stop @wheel.stop>
        <div class="ly-sp-h">Блок {{ storyBlock + 1 }} · Сессия {{ num(read.u, read.i) }}</div>
        <div class="ly-sp-t" :class="{ none: head(read.u).none }">{{ head(read.u).t }}</div>
        <div class="ly-sp-s">{{ head(read.u).s }}</div>
        <div class="ly-sp-m">
          <b class="ly-dot" :style="{ background: colors[read.u.phrase] }"></b>{{ read.u.phrase }}
          · {{ dateOf(read.u) }} · {{ read.u.churn >= 0 ? 'отток в блоке ' + (read.u.churn + 1) : noChurnTxt(read.u) }}
        </div>
        <div class="ly-sp-src">{{ srcOf(read.u) }}</div>
        <div v-if="read.near.length" class="ly-sp-n">
          <i v-for="k in read.near" :key="k" @click.stop="$emit('select-unit', k)">
            <b>{{ k < read.i ? 'выше' : 'ниже' }}:</b> {{ head(units[k]).t }}
          </i>
        </div>
      </div>

      <!-- окно распределений: когорта с тем же текстом, в текущем порядке ряда -->
      <div v-if="dist" class="ly-dwin" @mousedown.stop @wheel.stop>
        <div class="ly-dw-h">Распределение текста</div>
        <div class="ly-dw-t">{{ head(dist.u0).t }}</div>
        <div class="ly-dw-s">{{ head(dist.u0).s }}</div>
        <div class="ly-dw-n"><b>{{ dist.coh.length }}</b> {{ plural(dist.coh.length, CARDS) }} из {{ units.length }}</div>
        <div class="ly-dw-lab">по словосочетаниям</div>
        <div class="ly-dw-mix">
          <i v-for="m in dist.mix" :key="m.k"><b :style="{ background: colors[m.k] }"></b>{{ m.k }}<u>{{ m.n }}</u><s>{{ Math.round(m.n / dist.coh.length * 100) }} %</s></i>
        </div>
        <div class="ly-dw-lab">медиана блока оттока</div>
        <div class="ly-dw-med">
          <template v-if="dist.med !== null">
            <b>блок {{ dist.med }}</b>
            <span>медиана среза {{ dist.all }} · {{ dist.d > 0 ? '+' + dist.d : dist.d }} блока</span>
          </template>
          <span v-else>у этих сессий оттока нет</span>
        </div>
        <div class="ly-dw-lab">сессия × блоки — в текущем порядке ряда</div>
        <div class="ly-dw-grid" :class="{ open: distOpen }">
          <div class="ly-dw-r ly-dw-head">
            <i class="ly-dw-i">№</i>
            <i v-for="k in nB" :key="k" class="ly-dw-c" :class="{ med: k === dist.all }">{{ k }}</i>
          </div>
          <div v-for="x in dist.shown" :key="x.u.id" class="ly-dw-r" @click.stop="$emit('select-unit', x.i)">
            <i class="ly-dw-i" :style="{ color: colors[x.u.phrase] }">{{ num(x.u, x.i) }}</i>
            <i v-for="k in nB" :key="k" class="ly-dw-c" :class="{ on: k === x.hb, med: k === dist.all }"></i>
          </div>
        </div>
        <button v-if="dist.coh.length > LIM" type="button" class="ly-dw-b" @click.stop="distOpen = !distOpen">
          {{ distOpen ? 'свернуть' : 'развернуть · ещё ' + (dist.coh.length - LIM) }}
        </button>
      </div>

      <!-- окно «влияние»: разборы заголовка и словосочетания, каждый своей кнопкой -->
      <div v-if="inf" class="ly-iwin" @mousedown.stop @wheel.stop>
        <div class="ly-iw-h">
          <b>Влияние факторов</b>
          <span>исход — средний блок оттока · всего {{ F.n }} · общее среднее {{ F.G.toFixed(2) }}</span>
        </div>
        <div class="ly-iw-tabs">
          <button v-for="t in TABS" :key="t[0]" type="button" class="ly-iw-b" :class="{ on: infOn[t[0]] }"
                  @click.stop="setInf({ [t[0]]: !infOn[t[0]] })">{{ t[1] }}</button>
          <button type="button" class="ly-iw-b link" @click.stop="link">связка</button>
        </div>
        <div v-if="!F.n" class="ly-iw-n">Нет сессий с оттоком — разбирать нечего.</div>
        <template v-else>
          <!-- выводы простым языком -->
          <div v-if="infOn.say" class="ly-iw-sec say">
            <div class="ly-iw-t">Что это значит</div>
            <div v-for="(v, k) in V.out" :key="k" class="ly-iw-v" :class="v.t"><b>{{ v.h }}</b><span>{{ v.s }}</span></div>
            <div v-if="V.acts.length" class="ly-iw-do">
              <b>Что делать</b>
              <ol><li v-for="(a, k) in V.acts" :key="k">{{ a }}</li></ol>
            </div>
            <div class="ly-iw-n">
              Вывод собирается по правилам от чисел на соседних вкладках, без нейросети: доля разброса
              до {{ Math.round(SAY.слабо * 100) }} % — «почти не влияет», свыше {{ Math.round(SAY.заметно * 100) }} % — «главный»;
              вклад состава от {{ SAY.составМал.toFixed(2) }} блока считается заметным, от {{ SAY.составБол.toFixed(2) }} —
              запрещающим сравнение по сырым числам; различимая разница {{ F.mde.toFixed(2) }} блока при типичной
              клетке в {{ F.nTyp }} {{ plural(F.nTyp, SESS) }}.
            </div>
          </div>

          <!-- разложение вариации -->
          <div v-if="infOn.var" class="ly-iw-sec">
            <div class="ly-iw-t">Разложение вариации</div>
            <template v-for="b in varBars" :key="b.lab">
              <div class="ly-iw-lab">{{ b.lab }}</div>
              <div class="ly-iw-bar">
                <i v-for="s in b.seg" :key="s.c" :class="s.c" :style="{ width: (s.x * 100).toFixed(2) + '%' }" :title="s.t + ': ' + pct(s.x)"></i>
              </div>
              <div class="ly-iw-key"><u v-for="s in b.seg" :key="s.c"><b :class="s.c"></b>{{ s.t }} {{ pct(s.x) }}</u></div>
            </template>
            <div class="ly-iw-n">Каждый шаг снимает свою часть с остатка предыдущего, поэтому доли в сумме дают 100 %.
              Первому фактору достаётся и та часть, которую он делит с соседом: разница между двумя прогонами и есть
              общая, спорная доля.</div>
          </div>

          <!-- веер наклонов -->
          <div v-if="infOn.fan" class="ly-iw-sec">
            <div class="ly-iw-t">Веер наклонов</div>
            <svg class="ly-iw-fan" :viewBox="'0 0 ' + FAN.W + ' ' + FAN.H" width="100%">
              <line class="ly-ax" :x1="FAN.PADL - 6" :y1="fan.y(F.G)" :x2="FAN.W - 4" :y2="fan.y(F.G)" />
              <g v-for="h in fan.lines" :key="h.t" class="ly-iw-l">
                <title>{{ h.t }} · {{ h.n }}</title>
                <line v-for="(s, k) in h.segs" :key="'l' + k" :x1="s[0]" :y1="s[1]" :x2="s[2]" :y2="s[3]" :stroke-width="h.w" />
                <circle v-for="(c, k) in h.pts" :key="'c' + k" :cx="c[0]" :cy="c[1]" :r="c[2]" />
              </g>
              <polyline class="ly-marg" :points="fan.marg" />
              <circle v-for="(p, i) in F.phs" :key="'m' + i" :cx="fan.xs[i]" :cy="fan.y(p.m)" r="4" :fill="colors[p.ph]" />
              <text v-for="(p, i) in fan.labs" :key="'x' + i" class="ly-xl" :x="fan.xs[F.phs.indexOf(p)]" :y="FAN.H - 4" :fill="colors[p.ph]"
                    :text-anchor="p === F.phs[0] ? 'start' : p === F.phs[F.phs.length - 1] ? 'end' : 'middle'">{{ p.ph }}</text>
              <text class="ly-yl" x="2" :y="fan.y(F.lo) + 4">{{ F.lo }}</text>
              <text class="ly-yl" x="2" :y="fan.y(F.hi) + 4">{{ F.hi }}</text>
            </svg>
            <div v-if="fan.labs.length < F.phs.length" class="ly-iw-n">Фраз {{ F.phs.length }} — подписаны только крайние, остальные в подсказке матрицы.</div>
            <div class="ly-iw-n">Каждая тонкая линия — один заголовок, толщина по числу сессий, жирная — маргинал фразы.
              Линии параллельны — фраза действует на все заголовки одинаково, и её эффект можно читать одним числом.
              Пересекаются — единого эффекта нет.</div>
          </div>

          <!-- матрица заголовок × фраза -->
          <div v-if="infOn.mat" class="ly-iw-sec">
            <div class="ly-iw-t">Заголовок × словосочетание</div>
            <div class="ly-iw-mat">
              <div class="ly-iw-r ly-iw-head">
                <i class="ly-iw-nm"></i>
                <i v-for="p in mat.phs" :key="p.ph" class="ly-iw-c" :style="{ color: colors[p.ph] }" :title="p.ph">{{ p.ph }}</i>
                <i class="ly-iw-c mg">все</i>
              </div>
              <div v-for="h in F.heads" :key="h.t" class="ly-iw-r">
                <i class="ly-iw-nm" :class="{ none: h.none }" :title="h.t">{{ h.t }}</i>
                <template v-for="c in mat.cellsOf(h)" :key="c.ph">
                  <i v-if="c.c" class="ly-iw-c val" :class="{ thin: c.c.n <= 2, sel: isCell(h.t, c.ph) }"
                     :style="devStyle(c.c.m)" :title="h.t + ' · ' + c.ph + ': среднее ' + c.c.m.toFixed(2) + ', сессий ' + c.c.n"
                     @click.stop="pickCell(h.t, c.ph)">{{ c.c.m.toFixed(1) }}<u>{{ c.c.n }}</u></i>
                  <i v-else class="ly-iw-c empty" title="нет сессий">—</i>
                </template>
                <i class="ly-iw-c mg">{{ h.m.toFixed(1) }}<u>{{ h.n }}</u></i>
              </div>
              <div class="ly-iw-r ly-iw-foot">
                <i class="ly-iw-nm">сырое среднее</i>
                <i v-for="p in mat.phs" :key="p.ph" class="ly-iw-c">{{ p.m.toFixed(2) }}<u>{{ p.n }}</u></i>
                <i class="ly-iw-c mg">{{ F.G.toFixed(2) }}</i>
              </div>
              <div class="ly-iw-r ly-iw-foot">
                <i class="ly-iw-nm">стандартизованное</i>
                <i v-for="p in mat.phs" :key="p.ph" class="ly-iw-c">{{ p.std === null ? '—' : p.std.toFixed(2) }}</i>
                <i class="ly-iw-c mg"></i>
              </div>
              <div class="ly-iw-r ly-iw-foot">
                <i class="ly-iw-nm">вклад состава</i>
                <i v-for="p in mat.phs" :key="p.ph" class="ly-iw-c" :class="{ warn: Math.abs(p.d) >= 0.15 }">{{ sign(p.d) }}</i>
                <i class="ly-iw-c mg"></i>
              </div>
            </div>
            <div v-if="mat.more" class="ly-iw-n">ещё {{ mat.more }} {{ plural(mat.more, PHRS) }} с меньшим числом сессий не показаны.</div>
            <div class="ly-iw-n">Клетка — средний блок, снизу число сессий; клетки с одной-двумя сессиями приглушены.
              «Вклад состава» — насколько сырое среднее фразы сдвинуто тем, какие заголовки ей чаще достаются.
              Клик по клетке — разложить её в каскаде.</div>
          </div>

          <!-- каскад вкладов -->
          <div v-if="infOn.casc && casc" class="ly-iw-sec">
            <div class="ly-iw-t">Каскад вкладов</div>
            <div class="ly-iw-cs"><b>{{ casc.t }}</b> · <span :style="{ color: colors[casc.ph] }">{{ casc.ph }}</span>
              · {{ casc.n }} {{ plural(casc.n, SESS) }}</div>
            <div class="ly-iw-st"><i>общее среднее</i><b>{{ F.G.toFixed(2) }}</b><s></s></div>
            <div v-for="s in casc.steps" :key="s.lab" class="ly-iw-st">
              <i>{{ s.lab }}</i><b>{{ sign(s.v) }}</b>
              <s><u :class="s.c" :style="s.style"></u></s>
            </div>
            <div class="ly-iw-st tot"><i>факт</i><b>{{ casc.m.toFixed(2) }}</b><s></s></div>
            <div class="ly-iw-n">Аддитивная подгонка «общее + поправка заголовка + поправка фразы»; остаток до факта —
              взаимодействие именно этой пары.</div>
          </div>

          <!-- парные сравнения -->
          <div v-if="infOn.pair" class="ly-iw-sec">
            <div class="ly-iw-t">Пары фраз внутри заголовка</div>
            <div v-for="pr in pairs.list" :key="pr.a + '\u0001' + pr.b" class="ly-iw-pr">
              <i><b :style="{ background: colors[pr.a] }"></b>{{ pr.a }} → <b :style="{ background: colors[pr.b] }"></b>{{ pr.b }}</i>
              <s title="разница сырых маргиналов">сырое {{ sign(pr.raw) }}</s>
              <u title="средняя разница по заголовкам, где есть обе фразы">в парах {{ pr.d === null ? '—' : sign(pr.d) }} <em>{{ pr.k }}</em></u>
            </div>
            <div v-if="pairs.more" class="ly-iw-n">ещё {{ pairs.more }} {{ plural(pairs.more, PAIRS) }} — показаны пары с наибольшим числом общих заголовков.</div>
            <div class="ly-iw-n">Берём только заголовки, где встретились обе фразы, и усредняем разницу внутри каждого.
              Состав тут не при чём по построению: если «в парах» сильно отличается от «сырого», разрыв между фразами
              был про состав, а не про фразу.</div>
          </div>
        </template>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { twoFactor, verdicts, SAY, phraseColors, headOf, noChurnTxt, dayLabel } from '@/utils/layers';
import { plural } from '@/utils/segments';

const CARDS = ['карточка', 'карточки', 'карточек'];
const SESS = ['сессия', 'сессии', 'сессий'];
const TABS = [['say', 'выводы'], ['mat', 'матрица'], ['fan', 'веер'], ['var', 'разложение'], ['casc', 'каскад'], ['pair', 'пары']];
const FAN = { W: 300, H: 150, PADL: 26, PADB: 18 };
const LIM = 8;
const FAN_LABELS = 12;
// Матрица heads × phs: при сотнях фраз это тысячи клеток — берём самые массовые фразы.
const MAT_MAX = 40;
const PAIRS = ['пара', 'пары', 'пар'];
const PHRS = ['словосочетание', 'словосочетания', 'словосочетаний'];
// Пар фраз n·(n−1)/2: у сегмента «вся таблица» их десятки тысяч, в окно столько не влезет.
const PAIRS_MAX = 60;
// нижняя медиана — как в разборе заголовков (headStats)
const lowMed = a => { const z = a.slice().sort((x, y) => x - y); return z.length ? z[(z.length - 1) >> 1] : null; };
const SRC = { log: 'записано в сессии', snapshot: 'восстановлено по снимку', current: 'по текущим правилам, показ не записан' };

export default {
  name: 'LayerAnalysis',
  props: {
    units: { type: Array, default: () => [] },
    layout: { type: Object, default: null },
    storyBlock: { type: Number, default: null },
    frontIndex: { type: Number, default: null },
    numOf: { type: Function, default: null },         // сессия → хронологический номер, как в метке карточки
    panels: { type: Object, default: () => ({}) },    // {read, dist, inf}
    stats: { type: Object, default: null },
    // вкладки окна «влияние» хранит сцена (KEEP): выбор переживает выход из блока, как anInfOn в П
    infOn: { type: Object, default: () => ({ say: false, mat: false, fan: false, var: false, casc: false, pair: false }) },
  },
  emits: ['select-unit', 'inf'],
  data() {
    return {
      stageEl: null, distOpen: false, cell: null,
      CARDS, SESS, TABS, FAN, LIM, SAY, PAIRS, PHRS,
    };
  },
  computed: {
    live() { return this.storyBlock !== null && this.units.length > 0; },
    front() {
      const i = this.frontIndex;
      return i !== null && i >= 0 && i < this.units.length ? i : null;
    },
    // Цвета — тем же рангом фраз, что у сцены: сцена считает phraseColors по тем же сессиям.
    colors() { return phraseColors(this.units.map(u => u.phrase)); },
    // число блоков: headStats режет гистограмму ровно по nBlocks столбца
    nB() {
      return (this.stats && this.stats.hist && this.stats.hist.length)
        || this.units.reduce((m, u) => Math.max(m, u.reached + 1, u.churn + 1), 0);
    },
    read() {
      // окно распределений занимает то же место справа — панель чтения уступает ему
      if (!this.live || !this.panels.read || this.panels.dist || this.front === null) return null;
      const i = this.front;
      return { i, u: this.units[i], near: [i - 1, i + 1].filter(k => k >= 0 && k < this.units.length) };
    },
    dist() {
      if (!this.live || !this.panels.dist) return null;
      const u0 = this.units[this.front === null ? 0 : this.front];
      const key = this.keyOf(u0);
      const coh = [];
      this.units.forEach((u, i) => { if (this.keyOf(u) === key) coh.push({ u, i, hb: u.churn + 1 }); });
      const phc = Object.create(null);
      coh.forEach(x => { phc[x.u.phrase] = (phc[x.u.phrase] || 0) + 1; });
      // без оттока меры нет — в медианы такие сессии не входят
      const med = lowMed(coh.filter(x => x.hb > 0).map(x => x.hb));
      const all = lowMed(this.units.filter(u => u.churn >= 0).map(u => u.churn + 1));
      return { u0, coh, med, all, d: med !== null && all !== null ? med - all : 0,
        mix: Object.keys(phc).map(k => ({ k, n: phc[k] })),
        shown: this.distOpen ? coh : coh.slice(0, LIM) };
    },
    inf() { return this.live && !!this.panels.inf; },
    F() { return this.inf ? twoFactor(this.units) : null; },
    V() { return this.F && this.F.n ? verdicts(this.F) : { out: [], acts: [] }; },
    varBars() {
      const bar = (v, lab) => ({ lab, seg: [
        { x: v.a, t: v.first === 't' ? 'заголовок' : 'фраза', c: 'ly-s1' },
        { x: v.b, t: v.first === 't' ? 'фраза сверх' : 'заголовок сверх', c: 'ly-s2' },
        { x: v.ab, t: 'взаимодействие', c: 'ly-s3' },
        { x: v.e, t: 'шум', c: 'ly-s4' },
      ] });
      return [bar(this.F.varT, 'сначала заголовок'), bar(this.F.varPh, 'сначала фраза')];
    },
    fan() {
      const F = this.F, { W, H, PADL, PADB } = FAN;
      const r1 = v => Number(v.toFixed(1));
      const xs = F.phs.map((_, i) => r1(PADL + (W - PADL - 8) * (F.phs.length === 1 ? 0.5 : i / (F.phs.length - 1))));
      const y = m => r1(6 + (H - PADB - 6) * (m - F.lo) / Math.max(0.001, F.hi - F.lo));
      const lines = F.heads.map(h => {
        const pts = h.cells.map((c, i) => (c.c ? [xs[i], y(c.c.m), r1(Math.max(1.6, Math.min(4, Math.sqrt(c.c.n))))] : null));
        const segs = [];
        for (let i = 1; i < pts.length; i++) if (pts[i - 1] && pts[i]) segs.push([pts[i - 1][0], pts[i - 1][1], pts[i][0], pts[i][1]]);
        return { t: h.t, n: h.n, w: Math.max(1, Math.min(4, h.n / 6)).toFixed(1), segs, pts: pts.filter(Boolean) };
      });
      // Больше FAN_LABELS подписей на 300 px сливаются в полосу — тогда подписываем только края.
      const labs = F.phs.length <= FAN_LABELS ? F.phs : [F.phs[0], F.phs[F.phs.length - 1]];
      return { xs, y, lines, labs, marg: F.phs.map((p, i) => `${xs[i]},${y(p.m)}`).join(' ') };
    },
    mat() {
      const F = this.F;
      if (F.phs.length <= MAT_MAX) return { phs: F.phs, cellsOf: h => h.cells, more: 0 };
      const top = new Set(F.phs.slice().sort((a, b) => b.n - a.n).slice(0, MAT_MAX).map(p => p.ph));
      // порядок столбцов прежний (как у twoFactor), выпадают только редкие фразы
      return { phs: F.phs.filter(p => top.has(p.ph)), cellsOf: h => h.cells.filter(c => top.has(c.ph)), more: F.phs.length - top.size };
    },
    casc() {
      const F = this.F;
      let t = this.cell && this.cell.t, ph = this.cell && this.cell.ph;
      if (!this.cell || !F.cell(t, ph)) {
        // по умолчанию — клетка, дальше всех ушедшая от общего среднего
        let best = null;
        F.heads.forEach(h => h.cells.forEach(c => {
          if (c.c && c.c.n >= 2 && (!best || Math.abs(c.c.m - F.G) > Math.abs(best.m - F.G))) best = { t: h.t, ph: c.ph, m: c.c.m };
        }));
        if (!best) return null;
        ({ t, ph } = best);
      }
      const c = F.cell(t, ph);
      const h = F.heads.find(x => x.t === t), p = F.phs.find(x => x.ph === ph);
      const inter = c.m - (F.G + h.a + p.b);
      const steps = [{ lab: 'заголовок', v: h.a, c: 'ly-s1' }, { lab: 'словосочетание', v: p.b, c: 'ly-s2' },
        { lab: 'взаимодействие', v: inter, c: 'ly-s3' }];
      const mx = Math.max(0.25, ...steps.map(s => Math.abs(s.v)));
      steps.forEach(s => {
        const w = Math.abs(s.v) / mx * 50;
        s.style = `width:${w.toFixed(1)}%; margin-left:${s.v >= 0 ? 50 : 50 - w}%`;
      });
      return { t, ph, n: c.n, m: c.m, steps };
    },
    pairs() {
      const all = this.F.pairs;
      if (all.length <= PAIRS_MAX) return { list: all, more: 0 };
      // ponytail: обрезка по числу общих заголовков; полный список — отдельной таблицей, если попросят
      const list = all.filter(p => p.k > 0).sort((a, b) => b.k - a.k).slice(0, PAIRS_MAX);
      return { list, more: all.length - list.length };
    },
  },
  watch: {
    // новая когорта — список снова свёрнут
    storyBlock() { this.distOpen = false; this.cell = null; },
    'panels.dist'() { this.distOpen = false; },
  },
  mounted() {
    this.stageEl = this.$el.closest('.ly-stage');
  },
  methods: {
    num(u, i) { return this.numOf ? this.numOf(u) : i + 1; },
    plural,
    keyOf(u) { return headOf(u).key; },
    head: headOf,
    noChurnTxt,
    srcOf(u) { return SRC[u.shownSource] || 'нет данных о показе'; },
    // day — московские сутки от эпохи, поэтому UTC-дата day·сутки и есть московская
    dateOf(u) { return dayLabel(u.day); },
    pct(v) { return Math.round(v * 100) + ' %'; },
    sign(v) { return (v > 0 ? '+' : '') + v.toFixed(2); },
    // цвет клетки — отклонение от общего среднего: позже красным, раньше синим
    devStyle(m) {
      const F = this.F, d = m - F.G;
      const a = Math.min(1, Math.abs(d) / Math.max(0.4, (F.hi - F.lo) / 2));
      return `background:color-mix(in srgb, var(${d >= 0 ? '--div-pos' : '--div-neg'}) ${((0.06 + a * 0.8) * 100).toFixed(1)}%, transparent);`;
    },
    isCell(t, ph) { return !!this.cell && this.cell.t === t && this.cell.ph === ph; },
    pickCell(t, ph) {
      this.cell = this.isCell(t, ph) ? null : { t, ph };
      this.setInf({ casc: true });
    },
    link() { this.setInf({ say: true, mat: true, fan: true, var: true }); },
    setInf(patch) { this.$emit('inf', { ...this.infOn, ...patch }); },
  },
};
</script>

<style scoped>
.ly-analysis { display: contents; }
.ly-spanel { position: absolute; right: 14px; top: 14px; width: 290px; z-index: 6; pointer-events: auto;
  background: var(--surface-1); border: 1px solid var(--border); border-radius: 14px;
  padding: 12px 14px; box-shadow: 0 12px 34px var(--shadow-xs); }
.ly-sp-h { font-size: 10.5px; letter-spacing: .04em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 6px; }
.ly-sp-t { font-size: 15px; font-weight: 650; line-height: 1.25; color: var(--text-primary); }
.ly-sp-t.none { color: var(--text-muted); font-weight: 400; }
.ly-sp-s { font-size: 12px; color: var(--text-secondary); line-height: 1.4; margin-top: 5px; }
.ly-sp-m { font-size: 11px; color: var(--text-muted); margin-top: 8px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.ly-sp-src { font-size: 10px; color: var(--text-muted); margin-top: 3px; }
.ly-dot { width: 8px; height: 8px; border-radius: 50%; flex: 0 0 auto; }
.ly-sp-n { margin-top: 10px; border-top: 1px dashed var(--border); padding-top: 8px; display: flex; flex-direction: column; gap: 4px; }
.ly-sp-n i { font-style: normal; font-size: 11px; color: var(--text-muted); cursor: pointer;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ly-sp-n i:hover { color: var(--text-primary); }
.ly-sp-n i b { font-weight: 650; color: var(--text-secondary); }

.ly-dwin { position: absolute; right: 0; top: 0; bottom: 0; width: 328px; z-index: 8; overflow: auto;
  background: var(--page); border-left: 1px solid var(--grid); padding: 14px 14px 16px; pointer-events: auto; box-sizing: border-box; }
.ly-dw-h { font-size: 10px; letter-spacing: .05em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 6px; }
.ly-dw-t { font-size: 15px; font-weight: 650; line-height: 1.25; color: var(--text-primary); }
.ly-dw-s { font-size: 11.5px; color: var(--text-muted); line-height: 1.35; margin-top: 2px; }
.ly-dw-n { margin-top: 9px; font-size: 11.5px; color: var(--text-secondary); }
.ly-dw-n b { font-size: 16px; font-weight: 700; color: var(--text-primary); }
.ly-dw-lab { margin-top: 12px; margin-bottom: 4px; font-size: 9.5px; letter-spacing: .04em; text-transform: uppercase; color: var(--text-muted); }
.ly-dw-mix { display: flex; flex-direction: column; gap: 3px; }
.ly-dw-mix i { display: flex; align-items: center; gap: 6px; font-style: normal; font-size: 11.5px; color: var(--text-secondary); }
.ly-dw-mix i b { width: 8px; height: 8px; border-radius: 999px; flex: 0 0 auto; }
.ly-dw-mix i u { text-decoration: none; margin-left: auto; font-weight: 700; color: var(--text-primary); }
.ly-dw-mix i s { text-decoration: none; width: 42px; text-align: right; color: var(--text-muted); }
.ly-dw-med { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.ly-dw-med b { font-size: 13px; font-weight: 700; }
.ly-dw-med span { font-size: 10.5px; color: var(--text-muted); }
.ly-dw-grid { display: flex; flex-direction: column; gap: 2px; }
.ly-dw-grid.open { max-height: 288px; overflow: auto; padding-right: 4px; }
.ly-dw-r { display: flex; align-items: center; gap: 2px; border-radius: 4px; cursor: pointer; }
.ly-dw-r:hover { background: var(--surface-1); }
.ly-dw-head { cursor: default; color: var(--text-muted); font-size: 9px; }
.ly-dw-i { flex: 0 0 30px; font-style: normal; font-size: 10px; text-align: right; padding-right: 5px; font-variant-numeric: tabular-nums; }
.ly-dw-c { flex: 1 1 0; height: 14px; border-radius: 3px; font-style: normal; text-align: center; line-height: 14px;
  box-shadow: inset 0 0 0 1px var(--border); }
.ly-dw-head .ly-dw-c { box-shadow: none; }
.ly-dw-c.med { box-shadow: inset 0 0 0 1px var(--border), inset 2px 0 0 var(--text-muted); }
.ly-dw-c.on { background: var(--accent); }
.ly-dw-b { margin-top: 8px; width: 100%; font: inherit; font-size: 11px; cursor: pointer; background: var(--surface-1);
  border: 1px solid var(--border); border-radius: 999px; padding: 4px 10px; color: var(--text-secondary); }
.ly-dw-b:hover { color: var(--text-primary); border-color: var(--text-muted); }

.ly-iwin { position: absolute; left: 0; top: 0; bottom: 0; width: 372px; z-index: 9; overflow: auto; box-sizing: border-box;
  background: var(--page); border-right: 1px solid var(--grid); padding: 14px 14px 18px; pointer-events: auto; }
.ly-iw-h b { display: block; font-size: 13px; font-weight: 650; }
.ly-iw-h span { display: block; margin-top: 2px; font-size: 10px; color: var(--text-muted); }
.ly-iw-tabs { display: flex; flex-wrap: wrap; gap: 4px; margin: 9px 0 4px; }
.ly-iw-b { font: inherit; font-size: 10.5px; cursor: pointer; background: var(--surface-1); border: 1px solid var(--border);
  border-radius: 999px; padding: 2px 9px; color: var(--text-secondary); }
.ly-iw-b:hover { color: var(--text-primary); border-color: var(--text-muted); }
.ly-iw-b.on { background: var(--accent); border-color: var(--accent); color: var(--on-accent); }
.ly-iw-b.link { margin-left: auto; border-style: dashed; }
.ly-iw-sec { margin-top: 14px; padding-top: 11px; border-top: 1px solid var(--grid); }
.ly-iw-sec.say { border-top: 0; padding-top: 0; margin-top: 10px; }
.ly-iw-t { font-size: 11.5px; font-weight: 650; margin-bottom: 7px; }
.ly-iw-lab { font-size: 9.5px; color: var(--text-muted); margin: 6px 0 3px; }
.ly-iw-n { margin-top: 7px; font-size: 9.5px; color: var(--text-muted); line-height: 1.4; }
.ly-iw-bar { display: flex; height: 13px; border-radius: 4px; overflow: hidden; background: var(--surface-1); box-shadow: inset 0 0 0 1px var(--border); }
.ly-iw-bar i { display: block; height: 100%; }
.ly-iw-key { display: flex; flex-wrap: wrap; gap: 3px 10px; margin-top: 4px; }
.ly-iw-key u { text-decoration: none; font-size: 9.5px; color: var(--text-secondary); display: flex; align-items: center; gap: 4px; }
.ly-iw-key u b { width: 8px; height: 8px; border-radius: 2px; }
.ly-s1 { background: var(--accent); } .ly-s2 { background: var(--ph0); }
.ly-s3 { background: var(--ph1); } .ly-s4 { background: var(--grid); }
.ly-iw-fan { display: block; background: var(--surface-1); border: 1px solid var(--border); border-radius: 8px; }
.ly-iw-l line { stroke: var(--text-secondary); opacity: .42; stroke-linecap: round; }
.ly-iw-l circle { fill: var(--text-secondary); opacity: .5; }
.ly-iw-l:hover line { stroke: var(--text-primary); opacity: 1; }
.ly-iw-fan .ly-marg { fill: none; stroke: var(--text-primary); stroke-width: 2.4; opacity: .9; }
.ly-iw-fan .ly-ax { stroke: var(--text-muted); stroke-dasharray: 3 3; opacity: .6; }
.ly-iw-fan .ly-xl { font-size: 9px; }
.ly-iw-fan .ly-yl { font-size: 8px; fill: var(--text-muted); }
/* у сегмента «вся таблица» фраз сотни — матрица листается вбок, а не сжимается в нитки */
.ly-iw-mat { overflow-x: auto; }
.ly-iw-r { display: flex; align-items: center; gap: 2px; margin-bottom: 2px; }
.ly-iw-head, .ly-iw-foot { color: var(--text-muted); font-size: 9px; }
.ly-iw-nm { flex: 0 0 132px; font-style: normal; font-size: 10px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ly-iw-nm.none { color: var(--text-muted); }
.ly-iw-c { flex: 1 0 34px; height: 18px; border-radius: 3px; font-style: normal; font-size: 10px; text-align: center;
  line-height: 18px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ly-iw-c.val { box-shadow: inset 0 0 0 1px var(--border); cursor: pointer; }
.ly-iw-c.val:hover { box-shadow: inset 0 0 0 2px var(--text-primary); }
.ly-iw-c.val.sel { box-shadow: inset 0 0 0 2px var(--accent); }
.ly-iw-c.thin { opacity: .42; }
.ly-iw-c.empty { color: var(--text-muted); opacity: .5; }
.ly-iw-c.mg { font-weight: 650; color: var(--text-primary); }
.ly-iw-c.warn { color: var(--div-pos); font-weight: 700; }
.ly-iw-c u { text-decoration: none; font-size: 8px; opacity: .65; }
.ly-iw-cs { font-size: 10.5px; color: var(--text-secondary); margin-bottom: 6px; }
.ly-iw-cs b { font-weight: 650; color: var(--text-primary); }
.ly-iw-st { display: flex; align-items: center; gap: 6px; font-size: 10.5px; margin-bottom: 3px; }
.ly-iw-st i { flex: 0 0 108px; font-style: normal; color: var(--text-secondary); }
.ly-iw-st b { flex: 0 0 46px; text-align: right; font-variant-numeric: tabular-nums; }
.ly-iw-st s { flex: 1 1 0; text-decoration: none; height: 9px; border-radius: 2px; background: var(--surface-1); position: relative; box-shadow: inset 0 0 0 1px var(--border); }
.ly-iw-st s u { display: block; height: 100%; border-radius: 2px; text-decoration: none; }
.ly-iw-st.tot b { font-weight: 700; }
.ly-iw-pr { display: flex; align-items: center; gap: 8px; font-size: 10px; margin-bottom: 4px; }
.ly-iw-pr i { flex: 1 1 0; font-style: normal; color: var(--text-secondary); display: flex; align-items: center; gap: 4px; min-width: 0; overflow: hidden; white-space: nowrap; }
.ly-iw-pr i b { width: 7px; height: 7px; border-radius: 999px; flex: 0 0 auto; }
.ly-iw-pr s { text-decoration: none; color: var(--text-muted); width: 74px; text-align: right; }
.ly-iw-pr u { text-decoration: none; width: 92px; text-align: right; font-weight: 650; }
.ly-iw-pr u em { font-style: normal; font-weight: 400; color: var(--text-muted); font-size: 9px; }
.ly-iw-v { border-left: 3px solid var(--grid); padding: 1px 0 3px 9px; margin-bottom: 8px; }
.ly-iw-v.warn { border-left-color: var(--ev-drop); }
.ly-iw-v.good { border-left-color: var(--ev-goal); }
.ly-iw-v.info { border-left-color: var(--accent); }
.ly-iw-v b { display: block; font-size: 11px; font-weight: 650; margin-bottom: 1px; }
.ly-iw-v span { display: block; font-size: 10.5px; line-height: 1.45; color: var(--text-secondary); }
.ly-iw-do { margin-top: 10px; background: var(--surface-1); border: 1px solid var(--border); border-radius: 9px; padding: 8px 10px; }
.ly-iw-do b { display: block; font-size: 11px; font-weight: 650; margin-bottom: 4px; }
.ly-iw-do ol { margin: 0; padding-left: 16px; }
.ly-iw-do li { font-size: 10.5px; line-height: 1.45; color: var(--text-secondary); margin-bottom: 4px; }
</style>
